import { c as _c } from "react/compiler-runtime";
import type { CellContext, InitialTableState } from '@tanstack/react-table';
import usePagination from '@mui/material/usePagination';
import NumberFlow from '@number-flow/react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { addMinutes, formatDuration, getYear, intervalToDuration } from 'date-fns';
import { pipe } from 'effect';
import { maxBy, sum } from 'es-toolkit';
import { produce } from 'immer';
import { useAtom, useAtomValue, useStore } from 'jotai';
import { useImmerAtom } from 'jotai-immer';
import { Fragment, useRef } from 'react';
import type { Subtitles } from '@/app/[locale]/subtitles/_api/os';
import type { SubtitleData } from '@/app/[locale]/subtitles/_components/columns';
import type { RowId } from '@/app/[locale]/subtitles/_utils';
import type { ColumnFilterFn } from '@/lib/table-utils';
import type { RowSelectionChangeFn } from '@/types/utils';
import type { tmdb } from '@sub-vocab/utils/types';
import { openSubtitlesQueryOptionsAtom, osSessionAtom } from '@/app/[locale]/subtitles/_api/os';
import { $api } from '@/app/[locale]/subtitles/_api/tmdb';
import { buildInitialTableState, initialTableStateFamily, mediaSubtitleFamily, osLanguageAtom } from '@/app/[locale]/subtitles/_atoms';
import { useCommonColumns } from '@/app/[locale]/subtitles/_components/columns';
import { RefetchButton } from '@/app/[locale]/subtitles/_components/menu-items';
import { getFileId } from '@/app/[locale]/subtitles/_utils';
import { SortIcon } from '@/components/my-icon/sort-icon';
import { TableGoToLastPage } from '@/components/my-table/go-to-last-page';
import { TablePagination } from '@/components/my-table/pagination';
import { TablePaginationSizeSelect } from '@/components/my-table/pagination-size-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Div } from '@/components/ui/html-elements';
import { OverflowTooltipText } from '@/components/ui/overflow-tooltip-text';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { HeaderTitle, TableDataCell, TableHeader, TableHeaderCell, TableHeaderCellRender, TableRow } from '@/components/ui/table-element';
import { filterFn, noFilter, sortBySelection } from '@/lib/table-utils';
import { useI18n } from '@/locales/client';
import { useClone } from '@sub-vocab/utils/hooks';
import { compareBy, customFormatDistance, findClosest, formatIntervalLocale, isNonEmptyArray, naturalNumLength } from '@sub-vocab/utils/lib';
type ExpandableRow<T> = T & {
  subRows?: T[];
};
type Episode = NonNullable<tmdb.paths['/3/tv/{series_id}/season/{season_number}']['get']['responses'][200]['content']['application/json']['episodes']>[number];
type TVSubtitleData = SubtitleData<Episode> & RowId;
type RowData = ExpandableRow<TVSubtitleData>;
function isCheckboxClickTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) {
    return false;
  }
  return target instanceof HTMLInputElement && target.type === 'checkbox' || Boolean(target.closest('[data-slot="checkbox"]'));
}
function useTVColumns(mediaId, t0, rootRef) {
  const $ = _c(23);
  const highestEpisodeNumber = t0 === undefined ? 0 : t0;
  const columnHelper = createColumnHelper();
  const store = useStore();
  const [osSession] = useAtom(osSessionAtom);
  let t1;
  if ($[0] !== rootRef?.current) {
    t1 = t2 => {
      const {
        header
      } = t2;
      const isSorted = header.column.getIsSorted();
      return <TableHeaderCell header={header} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1 select-none" onClick={e => {
          header.column.getToggleSortingHandler()?.(e);
          requestAnimationFrame(() => rootRef?.current?.scrollTo({
            top: 0
          }));
        }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Year" isSorted={isSorted} className="*:data-title:text-left" /></Div></TableHeaderCell>;
    };
    $[0] = rootRef?.current;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = columnHelper.accessor(_temp, {
    id: "year",
    header: t1,
    cell: _temp2
  });
  let t3;
  if ($[2] !== mediaId || $[3] !== osSession?.token || $[4] !== rootRef?.current || $[5] !== store) {
    t3 = t4 => {
      const {
        header: header_0,
        table
      } = t4;
      const {
        rows
      } = table.getRowModel();
      const parentRows = rows.filter(_temp4);
      const checked = parentRows.length > 0 && parentRows.every(_temp5);
      const isSorted_0 = header_0.column.getIsSorted();
      return <TableHeaderCell header={header_0} className="w-[.1%] [&:active:has(.child:not(:active))]:signal/active [&:active:has(.child:not(:active))+th]:signal/active"><Div className="justify-between gap-1.5 pr-1 pl-2 select-none signal/active:bg-background-active" onClick={e_0 => {
          header_0.column.getToggleSortingHandler()?.(e_0);
          requestAnimationFrame(() => rootRef?.current?.scrollTo({
            top: 0
          }));
        }}><div className="flex">{osSession?.token ? <Checkbox checked={checked} className="child" onClick={e_1 => {
              e_1.stopPropagation();
              store.set(mediaSubtitleFamily([mediaId]), produce(t5 => {
                const {
                  tableState
                } = t5;
                parentRows.forEach(t6 => {
                  const {
                    subRows,
                    id
                  } = t6;
                  if (subRows.length === 0) {
                    tableState.rowSelection[id] = !checked;
                  } else {
                    subRows.forEach((t7, index) => {
                      const {
                        id: id_0
                      } = t7;
                      tableState.rowSelection[id_0] = !checked && index === 0;
                    });
                  }
                });
              }));
            }} /> : <div className="child w-4" />}</div><SortIcon isSorted={isSorted_0} /></Div></TableHeaderCell>;
    };
    $[2] = mediaId;
    $[3] = osSession?.token;
    $[4] = rootRef?.current;
    $[5] = store;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = columnHelper.accessor(_temp3, {
    id: "action",
    sortingFn: sortBySelection,
    header: t3,
    cell: _temp7
  });
  let t5;
  if ($[7] !== highestEpisodeNumber) {
    t5 = row_5 => {
      const {
        season_number,
        episode_number
      } = row_5.subtitle.attributes.feature_details;
      if (season_number && episode_number) {
        return `S${season_number} E${String(episode_number).padStart(Math.min(naturalNumLength(highestEpisodeNumber), 2), "0")}`;
      }
    };
    $[7] = highestEpisodeNumber;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== rootRef?.current) {
    t6 = t7 => {
      const {
        header: header_1
      } = t7;
      const isSorted_1 = header_1.column.getIsSorted();
      return <TableHeaderCell header={header_1} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1 select-none" onClick={e_3 => {
          header_1.column.getToggleSortingHandler()?.(e_3);
          requestAnimationFrame(() => rootRef?.current?.scrollTo({
            top: 0
          }));
        }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Ep" isSorted={isSorted_1} className="*:data-title:text-left" /></Div></TableHeaderCell>;
    };
    $[9] = rootRef?.current;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = columnHelper.accessor(t5, {
    id: "season_episode",
    filterFn,
    sortingFn: compareBy(_temp8),
    header: t6,
    cell: _temp9
  });
  let t8;
  if ($[11] !== rootRef?.current) {
    t8 = t9 => {
      const {
        header: header_2
      } = t9;
      const isSorted_2 = header_2.column.getIsSorted();
      return <TableHeaderCell header={header_2} className="active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1" onClick={e_4 => {
          header_2.column.getToggleSortingHandler()?.(e_4);
          requestAnimationFrame(() => rootRef?.current?.scrollTo({
            top: 0
          }));
        }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Name" isSorted={isSorted_2} className="*:data-title:text-left" /></Div></TableHeaderCell>;
    };
    $[11] = rootRef?.current;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== rootRef) {
    t9 = ctx => <TvNameCell {...ctx} rootRef={rootRef} />;
    $[13] = rootRef;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = columnHelper.accessor(_temp10, {
    id: "movie_name",
    filterFn,
    header: t8,
    cell: t9
  });
  let t11;
  if ($[15] !== rootRef?.current) {
    t11 = t12 => {
      const {
        header: header_3
      } = t12;
      const isSorted_3 = header_3.column.getIsSorted();
      return <TableHeaderCell header={header_3} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1 select-none" onClick={e_5 => {
          header_3.column.getToggleSortingHandler()?.(e_5);
          requestAnimationFrame(() => rootRef?.current?.scrollTo({
            top: 0
          }));
        }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Run Time" isSorted={isSorted_3} className="*:data-title:text-left" /></Div></TableHeaderCell>;
    };
    $[15] = rootRef?.current;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = columnHelper.accessor(_temp11, {
    id: "runtime",
    header: t11,
    cell: _temp12
  });
  let t13;
  if ($[17] !== t10 || $[18] !== t12 || $[19] !== t2 || $[20] !== t4 || $[21] !== t7) {
    t13 = [t2, t4, t7, t10, t12];
    $[17] = t10;
    $[18] = t12;
    $[19] = t2;
    $[20] = t4;
    $[21] = t7;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  return t13;
}
function _temp12(t0) {
  const {
    cell: cell_2,
    getValue: getValue_1,
    row: row_10
  } = t0;
  const value_1 = getValue_1() || 0;
  const start = new Date(0);
  const end = addMinutes(start, value_1);
  return <TableDataCell cell={cell_2}><Div className="justify-end pr-px pl-0.5 tracking-[.03em] tabular-nums">{row_10.depth >= 1 ? null : formatDuration(intervalToDuration({
        start,
        end
      }), {
        locale: {
          formatDistance: customFormatDistance(formatIntervalLocale)
        }
      })}</Div></TableDataCell>;
}
function _temp11(row_9) {
  return row_9.media?.runtime;
}
function _temp10(row_8) {
  return row_8.media?.name ?? "";
}
function _temp9(t0) {
  const {
    cell: cell_1,
    getValue: getValue_0,
    row: row_7
  } = t0;
  const value_0 = getValue_0();
  return <TableDataCell cell={cell_1}><Div className="pr-px pl-1 tracking-[.04em] whitespace-nowrap tabular-nums">{row_7.depth >= 1 ? null : value_0}</Div></TableDataCell>;
}
function _temp8(row_6) {
  const {
    season_number: t0,
    episode_number: t1
  } = row_6.original.subtitle.attributes.feature_details;
  const season_number_0 = t0 === undefined ? 0 : t0;
  const episode_number_0 = t1 === undefined ? 0 : t1;
  return [season_number_0, episode_number_0];
}
function _temp7(t0) {
  const {
    row: row_4,
    cell: cell_0,
    onExpandedChange,
    onRowSelectionChange
  } = t0;
  const canExpand = row_4.getCanExpand();
  const isExpanded = row_4.getIsExpanded();
  const checked_0 = row_4.getIsSelected() || row_4.getIsSomeSelected() || row_4.getIsAllSubRowsSelected();
  return <TableDataCell cell={cell_0}><Div className="text-zinc-400"><div className={clsx("flex h-full grow items-center justify-between pr-1 pl-2", canExpand && "cursor-pointer")} onClick={event => {
        if (!canExpand || isCheckboxClickTarget(event.target)) {
          return;
        }
        onExpandedChange?.(isExpanded);
      }}><Checkbox className={clsx(row_4.depth >= 1 && "rounded-full sq:rounded-full sq:[corner-shape:round]!")} onClick={_temp6} checked={checked_0} onCheckedChange={checked_1 => {
          if (row_4.depth === 0) {
            const [row1] = row_4.subRows;
            if (row1) {
              onRowSelectionChange?.(checked_1, row1, "singleSubRow");
            } else {
              onRowSelectionChange?.(checked_1, row_4);
            }
          } else {
            onRowSelectionChange?.(checked_1, row_4, "singleSubRow");
          }
        }} />{"\u200B"}<button type="button" aria-label={isExpanded ? "Collapse episode" : "Expand episode"} disabled={!canExpand} className={clsx("flex size-4 items-center justify-center rounded-sm border-0 bg-transparent p-0 text-zinc-400 transition-colors dark:text-zinc-500", canExpand ? "cursor-pointer hover:text-zinc-600 dark:hover:text-zinc-300" : "invisible")}><svg className={clsx("icon-[lucide--chevron-right] size-3.5 transition-transform duration-200", isExpanded ? "rotate-90" : "")} /></button></div></Div></TableDataCell>;
}
function _temp6(e_2) {
  return e_2.stopPropagation();
}
function _temp5(row_3) {
  return row_3.getIsSelected() || row_3.getIsSomeSelected() || row_3.getIsAllSubRowsSelected();
}
function _temp4(row_2) {
  return row_2.depth === 0;
}
function _temp3(row_1) {
  return row_1.subtitle.id;
}
function _temp2(t0) {
  const {
    cell,
    getValue,
    row: row_0
  } = t0;
  const value = getValue();
  return <TableDataCell cell={cell}><Div className="justify-center tracking-[.03em] tabular-nums">{row_0.depth >= 1 ? null : value}</Div></TableDataCell>;
}
function _temp(row) {
  const air_date = row.media?.air_date;
  return air_date ? getYear(air_date) : row.subtitle.attributes.feature_details.year;
}
function TvNameSubRow(t0) {
  const $ = _c(3);
  const {
    rootRef,
    row
  } = t0;
  const value = row.original.subtitle.attributes.files[0]?.file_name || "";
  let t1;
  if ($[0] !== rootRef || $[1] !== value) {
    t1 = <OverflowTooltipText rootRef={rootRef} value={value} className="text-sm tracking-[.04em]" />;
    $[0] = rootRef;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function TvNameCell(t0) {
  const $ = _c(9);
  const {
    rootRef,
    cell,
    getValue,
    row
  } = t0;
  let t1;
  if ($[0] !== getValue) {
    t1 = getValue();
    $[0] = getValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const value = t1;
  let t2;
  if ($[2] !== rootRef || $[3] !== row || $[4] !== value) {
    t2 = <Div className="cursor-text py-1 pr-px pl-2.5 select-text" onClick={_temp13}>{row.depth === 0 ? <TvNameParentRow rootRef={rootRef} value={value} /> : <TvNameSubRow rootRef={rootRef} row={row} />}</Div>;
    $[2] = rootRef;
    $[3] = row;
    $[4] = value;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== cell || $[7] !== t2) {
    t3 = <TableDataCell cell={cell}>{t2}</TableDataCell>;
    $[6] = cell;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function _temp13(ev) {
  return ev.stopPropagation();
}
function TvNameParentRow(t0) {
  const $ = _c(3);
  const {
    rootRef,
    value
  } = t0;
  let t1;
  if ($[0] !== rootRef || $[1] !== value) {
    t1 = <OverflowTooltipText rootRef={rootRef} value={value} className="text-sm tracking-[.04em]" />;
    $[0] = rootRef;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/// keep-unique
const PAGE_SIZES = [5, 6, 10, 20, 40, 50, 100, 200] as const;
type Season = tmdb.paths['/3/tv/{series_id}/season/{season_number}']['get']['responses'][200]['content']['application/json'];
function subtitleEpisodeData(subtitles: Subtitles['Response']['data'], episodes: Season['episodes'] = []) {
  return pipe(subtitles.map(subtitle => {
    const {
      season_number,
      episode_number
    } = subtitle.attributes.feature_details;
    if (season_number && episode_number) {
      return {
        media: episodes.find(episode => episode.episode_number === episode_number && episode.season_number === season_number),
        subtitle
      };
    }
    return undefined;
  }).filter(Boolean), x => Object.groupBy(x, ({
    subtitle
  }) => {
    const {
      season_number,
      episode_number
    } = subtitle.attributes.feature_details;
    return `${season_number}0${episode_number}`;
  }), x => Object.values(x).filter(Boolean).filter(isNonEmptyArray).map(group => {
    group.sort(compareBy(i => [i.subtitle.attributes.download_count], -1));
    const [parent] = group;
    const {
      season_number,
      episode_number
    } = parent.subtitle.attributes.feature_details;
    return {
      ...parent,
      '~rowId': `${season_number}0${episode_number}`,
      subRows: group
    };
  }));
}
function episodeFilter(filterEpisode: string): ColumnFilterFn<RowData> {
  if (filterEpisode === 'all') {
    return noFilter;
  } else {
    return row => row.subtitle.attributes.feature_details.season_number === Number(filterEpisode);
  }
}
export function TVSubtitleFiles(t0) {
  const $ = _c(100);
  const {
    id
  } = t0;
  const t = useI18n();
  const store = useStore();
  let t1;
  if ($[0] !== id) {
    t1 = $api.queryOptions("get", "/3/tv/{series_id}", {
      params: {
        path: {
          series_id: id
        }
      }
    });
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: seriesDetail,
    isLoading: isSeriesDetailLoading
  } = useQuery(t1);
  let t2;
  if ($[2] !== seriesDetail?.seasons) {
    t2 = seriesDetail?.seasons ?? [];
    $[2] = seriesDetail?.seasons;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const seasons = t2.filter(_temp14);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      label: "All Seasons",
      value: "all"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const seasonItems = [t3, ...seasons.map(_temp15)];
  let t4;
  if ($[5] !== id) {
    t4 = season_1 => $api.queryOptions("get", "/3/tv/{series_id}/season/{season_number}", {
      params: {
        path: {
          series_id: id,
          season_number: season_1.season_number
        }
      }
    });
    $[5] = id;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: episodes
  } = useQueries({
    queries: seasons.map(t4),
    combine: _temp19
  });
  const totalEpisodes = sum(seasons.map(_temp20));
  const openSubtitlesQueryOptions = useAtomValue(openSubtitlesQueryOptionsAtom);
  const [language] = useAtom(osLanguageAtom);
  let t5;
  if ($[7] !== id || $[8] !== language || $[9] !== openSubtitlesQueryOptions) {
    t5 = season_3 => openSubtitlesQueryOptions({
      parent_tmdb_id: id,
      languages: language,
      per_page: 100,
      season_number: season_3.season_number
    });
    $[7] = id;
    $[8] = language;
    $[9] = openSubtitlesQueryOptions;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    data: subtitles,
    isPending: isSubtitlesPending,
    refetchAll
  } = useQueries({
    queries: seasons.map(t5),
    combine: _temp24
  });
  const isPending = isSeriesDetailLoading || isSubtitlesPending;
  let t6;
  if ($[11] !== subtitles) {
    t6 = maxBy(subtitles, _temp25);
    $[11] = subtitles;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const highestEpisode = t6;
  const highestEpisodeNumber = highestEpisode?.attributes.feature_details.episode_number;
  const rootRef = useRef(null);
  const commonColumns = useCommonColumns(rootRef);
  const tvColumns = useTVColumns(id, highestEpisodeNumber, rootRef);
  const columns = [...commonColumns, ...tvColumns];
  const dataRows = subtitleEpisodeData(subtitles, episodes);
  let t7;
  if ($[13] !== id) {
    t7 = [id];
    $[13] = id;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const [t8, setMediaSubtitleState] = useImmerAtom(mediaSubtitleFamily.useA(t7));
  const {
    episodeFilter: t9,
    tableState: mediaTableState
  } = t8;
  const filterEpisode = t9 === undefined ? "all" : t9;
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = buildInitialTableState({
      sorting: [{
        id: "season_episode",
        desc: false
      }],
      columnOrder: ["action", "year", "season_episode", "movie_name", "runtime", "language", "upload_date", "download_count"],
      pagination: {
        pageSize: findClosest(10, PAGE_SIZES)
      }
    } satisfies InitialTableState);
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== id) {
    t11 = [id, t10];
    $[16] = id;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const mediaSubtitleInitialTableStateAtom = initialTableStateFamily.useA(t11);
  const table = useClone(useReactTable({
    data: dataRows,
    columns,
    initialState: store.get(mediaSubtitleInitialTableStateAtom),
    state: {
      ...mediaTableState,
      columnFilters: [{
        id: "season_episode",
        value: episodeFilter(filterEpisode)
      }]
    },
    autoResetPageIndex: false,
    getRowId: getFileId,
    getRowCanExpand: _temp26,
    getSubRows: _temp27,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onStateChange: updater => {
      store.set(mediaSubtitleInitialTableStateAtom, typeof updater === "function" ? updater(tableState) : updater);
    }
  }));
  const tableState = table.getState();
  let t12;
  if ($[18] !== table) {
    t12 = table.getPageCount();
    $[18] = table;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = tableState.pagination.pageIndex + 1;
  let t14;
  if ($[20] !== t12 || $[21] !== t13) {
    t14 = {
      count: t12,
      page: t13
    };
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const {
    items
  } = usePagination(t14);
  let rowsFiltered;
  let t15;
  if ($[23] !== table) {
    rowsFiltered = table.getFilteredRowModel().rows.filter(_temp28);
    t15 = sum(rowsFiltered.map(_temp29));
    $[23] = table;
    $[24] = rowsFiltered;
    $[25] = t15;
  } else {
    rowsFiltered = $[24];
    t15 = $[25];
  }
  const totalSubtitles = t15;
  const allAvailableRowsMatch = rowsFiltered.length === totalEpisodes;
  let t16;
  if ($[26] !== setMediaSubtitleState) {
    t16 = (checked, row_3, mode) => {
      setMediaSubtitleState(t17 => {
        const {
          tableState: tableState_0
        } = t17;
        if (mode === "singleRow") {
          tableState_0.rowSelection = {};
        } else {
          if (mode === "singleSubRow") {
            const subRows = row_3.getParentRow()?.subRows ?? [];
            subRows.forEach(t18 => {
              const {
                id: id_0
              } = t18;
              tableState_0.rowSelection[id_0] = false;
            });
          }
        }
        tableState_0.rowSelection[row_3.id] = Boolean(checked);
      });
    };
    $[26] = setMediaSubtitleState;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const handleRowSelectionChange = t16;
  let t17;
  if ($[28] !== setMediaSubtitleState) {
    t17 = e => {
      setMediaSubtitleState(draft => {
        draft.episodeFilter = e;
      });
    };
    $[28] = setMediaSubtitleState;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SelectTrigger className="h-full! w-[unset] gap-0 px-2 py-0 text-xs tracking-[.03em] tabular-nums [--sq-r:.625rem]"><SelectValue placeholder="Select" /></SelectTrigger>;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const T0 = SelectContent;
  const t19 = "item-aligned";
  const t20 = "tracking-[.03em] tabular-nums";
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SelectItem className="pr-4 text-xs" value="all">All Seasons</SelectItem>;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const t22 = seasons.map(_temp30);
  let t23;
  if ($[32] !== T0 || $[33] !== t21 || $[34] !== t22) {
    t23 = <T0 position={t19} className={t20}>{t21}{t22}</T0>;
    $[32] = T0;
    $[33] = t21;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== isPending || $[37] !== refetchAll) {
    t24 = <RefetchButton refetch={refetchAll} isFetching={isPending} />;
    $[36] = isPending;
    $[37] = refetchAll;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== table) {
    t25 = table.getHeaderGroups().map(_temp32);
    $[39] = table;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== t25) {
    t26 = <TableHeader>{t25}</TableHeader>;
    $[41] = t25;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] !== table) {
    t27 = table.getRowModel();
    $[43] = table;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  const t28 = useClone(t27.rows);
  let t29;
  if ($[45] !== handleRowSelectionChange || $[46] !== t28) {
    let t30;
    if ($[48] !== handleRowSelectionChange) {
      t30 = (row_4, index) => <TableRow key={row_4.id} row={row_4} root={rootRef} index={index + 1} onRowSelectionChange={handleRowSelectionChange} />;
      $[48] = handleRowSelectionChange;
      $[49] = t30;
    } else {
      t30 = $[49];
    }
    t29 = t28.map(t30);
    $[45] = handleRowSelectionChange;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[50] !== t29) {
    t30 = <tbody>{t29}</tbody>;
    $[50] = t29;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t26 || $[53] !== t30) {
    t31 = <table className="relative min-w-full border-separate border-spacing-0">{t26}{t30}</table>;
    $[52] = t26;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== table) {
    t32 = <TableGoToLastPage table={table} />;
    $[55] = table;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  let t33;
  if ($[57] !== t31 || $[58] !== t32) {
    t33 = <div ref={rootRef} className="grow overflow-auto overflow-y-scroll">{t31}{t32}</div>;
    $[57] = t31;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== items || $[61] !== table) {
    t34 = <TablePagination items={items} table={table} rootRef={rootRef} />;
    $[60] = items;
    $[61] = table;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== table || $[64] !== tableState.pagination.pageSize) {
    t35 = <TablePaginationSizeSelect table={table} sizes={PAGE_SIZES} value={tableState.pagination.pageSize} />;
    $[63] = table;
    $[64] = tableState.pagination.pageSize;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  const t36 = `/${t("page")}`;
  let t37;
  if ($[66] !== t36) {
    t37 = <div className="px-1 whitespace-nowrap">{t36}</div>;
    $[66] = t36;
    $[67] = t37;
  } else {
    t37 = $[67];
  }
  let t38;
  if ($[68] !== t35 || $[69] !== t37) {
    t38 = <div className="flex grow items-center justify-end"><div className="flex items-center text-xs">{t35}{t37}</div></div>;
    $[68] = t35;
    $[69] = t37;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== t34 || $[72] !== t38) {
    t39 = <div className="flex w-full flex-wrap items-center justify-between gap-0.5 border-t border-t-zinc-200 py-1 pr-0.5 tracking-[.03em] tabular-nums dark:border-neutral-800">{t34}{t38}</div>;
    $[71] = t34;
    $[72] = t38;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  const t40 = rowsFiltered.length;
  const t41 = allAvailableRowsMatch && "hidden";
  let t42;
  if ($[74] !== t41) {
    t42 = clsx(t41);
    $[74] = t41;
    $[75] = t42;
  } else {
    t42 = $[75];
  }
  let t43;
  if ($[76] !== rowsFiltered.length || $[77] !== t42) {
    t43 = <NumberFlow value={t40} className={t42} locales="en-US" animated={true} isolate={true} />;
    $[76] = rowsFiltered.length;
    $[77] = t42;
    $[78] = t43;
  } else {
    t43 = $[78];
  }
  const t44 = allAvailableRowsMatch && "hidden";
  let t45;
  if ($[79] !== t44) {
    t45 = clsx(t44);
    $[79] = t44;
    $[80] = t45;
  } else {
    t45 = $[80];
  }
  let t46;
  if ($[81] !== t45) {
    t46 = <span className={t45}>{" of "}</span>;
    $[81] = t45;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  const t47 = !allAvailableRowsMatch;
  let t48;
  if ($[83] !== t47 || $[84] !== totalEpisodes) {
    t48 = <NumberFlow value={totalEpisodes} locales="en-US" animated={true} isolate={t47} />;
    $[83] = t47;
    $[84] = totalEpisodes;
    $[85] = t48;
  } else {
    t48 = $[85];
  }
  let t49;
  let t50;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <span>{" episodes"}</span>;
    t50 = <span className="text-neutral-400">, </span>;
    $[86] = t49;
    $[87] = t50;
  } else {
    t49 = $[86];
    t50 = $[87];
  }
  let t51;
  if ($[88] !== t43 || $[89] !== t46 || $[90] !== t48) {
    t51 = <span>{t43}{t46}{t48}{t49}{t50}</span>;
    $[88] = t43;
    $[89] = t46;
    $[90] = t48;
    $[91] = t51;
  } else {
    t51 = $[91];
  }
  let t52;
  if ($[92] !== totalSubtitles) {
    t52 = <NumberFlow value={totalSubtitles} locales="en-US" animated={true} isolate={true} />;
    $[92] = totalSubtitles;
    $[93] = t52;
  } else {
    t52 = $[93];
  }
  let t53;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <span>{" subtitles"}</span>;
    $[94] = t53;
  } else {
    t53 = $[94];
  }
  let t54;
  if ($[95] !== t52) {
    t54 = <span>{t52}{t53}</span>;
    $[95] = t52;
    $[96] = t54;
  } else {
    t54 = $[96];
  }
  let t55;
  if ($[97] !== t51 || $[98] !== t54) {
    t55 = <div className="flex w-full justify-center border-t border-solid border-t-zinc-200 bg-background dark:border-neutral-800"><div className="flex h-7 items-center gap-1.5 text-xs tracking-[.03em] tabular-nums">{t51}{t54}</div></div>;
    $[97] = t51;
    $[98] = t54;
    $[99] = t55;
  } else {
    t55 = $[99];
  }
  return <Fragment><Fragment><Fragment><div><div className="flex h-9 gap-1.5 p-1.5"><Select items={seasonItems} value={filterEpisode} onValueChange={t17}>{t18}{t23}</Select>{t24}</div></div>{t33}{t39}{t55}</Fragment></Fragment></Fragment>;
}
function _temp32(headerGroup) {
  return <tr key={headerGroup.id}>{headerGroup.headers.map(_temp31)}</tr>;
}
function _temp31(header) {
  return <TableHeaderCellRender key={header.id} header={header} />;
}
function _temp30(season_4) {
  return <SelectItem key={season_4.season_number} className="pr-4 text-xs" value={String(season_4.season_number)}>{`Season ${season_4.season_number}`}</SelectItem>;
}
function _temp29(row_2) {
  return row_2.subRows.length;
}
function _temp28(row_1) {
  return row_1.depth === 0 && row_1.subRows.length >= 1;
}
function _temp27(row_0) {
  return row_0?.subRows ?? [];
}
function _temp26(row) {
  return row?.subRows.length >= 1;
}
function _temp25(d) {
  return d.attributes.feature_details.episode_number ?? 0;
}
function _temp24(results_0) {
  return {
    data: results_0.map(_temp21).flat(),
    isPending: results_0.some(_temp22),
    refetchAll: () => {
      results_0.forEach(_temp23);
    }
  };
}
function _temp23(result_4) {
  return result_4.refetch();
}
function _temp22(result_3) {
  return result_3.isFetching;
}
function _temp21(result_2) {
  return result_2.data ?? [];
}
function _temp20(season_2) {
  return season_2.episode_count;
}
function _temp19(results) {
  return {
    data: results.map(_temp16).flat(),
    isPending: results.some(_temp17),
    refetchAll: () => {
      results.forEach(_temp18);
    }
  };
}
function _temp18(result_1) {
  return result_1.refetch();
}
function _temp17(result_0) {
  return result_0.isFetching;
}
function _temp16(result) {
  return result.data?.episodes ?? [];
}
function _temp15(season_0) {
  return {
    label: `Season ${season_0.season_number}`,
    value: String(season_0.season_number)
  };
}
function _temp14(season) {
  return season.season_number >= 1;
}